// Remove static import - we'll use dynamic import instead
// Logo reference for branding
import mjLogoPath from "../assets/mnj-logo.jpg";

export interface ExcelColumn {
  key: string;
  title: string;
  render?: (value: any) => string | number;
}

export interface ExcelExportOptions {
  filename: string;
  sheetName?: string;
  columns: ExcelColumn[];
  data: any[];
  includeBranding?: boolean;
  projectName?: string;
}

export interface ExcelImportOptions {
  expectedColumns: string[];
  sheetName?: string;
  headerRow?: number;
  preserveEmptyValues?: boolean; // Whether to preserve empty cells as null (default: true)
  skipValidation?: boolean; // Whether to skip all validation during import (default: false)
  columnMapping?: { [title: string]: string }; // Map Excel column titles to data keys
}

export interface ExcelImportResult<T = any> {
  data: T[];
  errors: string[];
  warnings: string[];
}

/**
 * Export data to Excel file
 */
export const exportToExcel = async (options: ExcelExportOptions): Promise<void> => {
  const { filename, sheetName = "Sheet1", columns, data, includeBranding = true, projectName } = options;

  try {
    const XLSX = await import('xlsx');

    // Prepare branding headers if enabled
    let brandingRows: string[][] = [];
    if (includeBranding) {
      brandingRows = [
        ["M & J EVANS CONSTRUCTION LIMITED", "", "", "LOGO: See M&J Company Logo"],
        ["The Groundworks Partner of Choice"],
        [sheetName.toUpperCase()],
        projectName ? [`Project: ${projectName}`] : [],
        [`Generated: ${new Date().toLocaleDateString()}`],
        [`Report Type: Bill of Quantities Summary`],
        [] // Empty row
      ].filter(row => row.length > 0); // Remove empty arrays
    }

    // Prepare headers
    const headers = columns.map((col) => col.title);

    const getNestedValue = (obj: any, path: string) => {
      return path.split(".").reduce((acc, key) => acc && acc[key], obj);
    };

    // Prepare data rows
    const rows = data.map((item) =>
      columns.map((col) => {
        const keys = col.key.split(".");
        if (keys.length > 0) {
          const value = getNestedValue(item, col.key);
          return col.render ? col.render(value) : value;
        } else {
          const value = item[col.key];
          return col.render ? col.render(value) : value;
        }
      })
    );

    // Combine branding, headers and data
    const worksheetData = [...brandingRows, headers, ...rows];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns
    const colWidths = columns.map((col, index) => {
      const brandingLengths = includeBranding ? brandingRows.map(row => String(row[index] || "").length) : [];
      const maxLength = Math.max(
        col.title.length,
        ...rows.map((row) => String(row[index] || "").length),
        ...brandingLengths
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    worksheet["!cols"] = colWidths;

    // Add header row styling for branding
    if (includeBranding && brandingRows.length > 0) {
      // Style the company name row (first row) as bold
      const companyNameCell = `A1`;
      if (worksheet[companyNameCell]) {
        worksheet[companyNameCell].s = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center" }
        };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate and download file
    const fileExtension = filename.toLowerCase().endsWith(".xlsx") ? "" : ".xlsx";
    XLSX.writeFile(workbook, `${filename}${fileExtension}`);
    
  } catch (error) {
    console.error("Error in exportToExcel:", error);
    alert(`Export failed: ${error.message}`);
    throw error;
  }
};

/**
 * Intelligently convert value to appropriate type
 */
const convertValueType = (value: any, preserveEmpty: boolean = true): any => {
  // Handle all empty value cases that XLSX might return
  if (value === null || value === undefined) {
    return null;
  }

  // Handle empty strings and whitespace-only strings
  if (value === "" || (typeof value === "string" && value.trim() === "")) {
    return preserveEmpty ? null : "";
  }

  // If it's already a number, round to 3 decimal places
  if (typeof value === "number") {
    return Math.round(value * 1000) / 1000;
  }

  // If it's already a boolean, keep it as is
  if (typeof value === "boolean") {
    return value;
  }

  // If it's a string, check if it should be converted to a number
  if (typeof value === "string") {
    const trimmed = value.trim();
    // At this point, trimmed cannot be empty (handled above)

    // Enhanced numeric detection patterns
    const patterns = [
      /^-?\d+$/, // Integer: 123, -456
      /^-?\d*\.?\d+$/, // Decimal: 123.45, .5, 123.
      /^-?\d{1,3}(,\d{3})*$/, // Integer with commas: 1,234
      /^-?\d{1,3}(,\d{3})*\.\d+$/, // Decimal with commas: 1,234.56
      /^-?\d+\.?\d*[eE][+-]?\d+$/, // Scientific notation: 1e5, 1.23E-4
      /^-?0x[0-9a-fA-F]+$/, // Hexadecimal: 0xFF, -0x10
      /^-?0b[01]+$/, // Binary: 0b101, -0b110
      /^-?0o[0-7]+$/, // Octal: 0o777, -0o123
      /^\d+%$/, // Percentage: 50%
    ];

    // Check if the trimmed string matches any numeric pattern
    const isNumeric = patterns.some((pattern) => pattern.test(trimmed));

    if (isNumeric) {
      let cleaned = trimmed;

      // Handle different number formats
      if (
        cleaned.includes(",") &&
        !cleaned.startsWith("0x") &&
        !cleaned.startsWith("0b") &&
        !cleaned.startsWith("0o")
      ) {
        // Remove commas from regular numbers
        cleaned = cleaned.replace(/,/g, "");
      }

      // Handle percentages
      if (cleaned.endsWith("%")) {
        cleaned = cleaned.slice(0, -1); // Remove % sign
        const percentNum = Number(cleaned);
        if (!isNaN(percentNum) && isFinite(percentNum)) {
          return Math.round((percentNum / 100) * 1000) / 1000; // Convert percentage to decimal and round to 3 decimals
        }
      }

      const num = Number(cleaned);
      if (!isNaN(num) && isFinite(num)) {
        return Math.round(num * 1000) / 1000; // Round to 3 decimal places
      }
    }
  }

  return value;
};

/**
 * Import data from Excel file
 */
export const importFromExcel = <T = any>(
  file: File,
  options: ExcelImportOptions
): Promise<ExcelImportResult<T>> => {
  return new Promise(async (resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        // Load XLSX dynamically
        const XLSX = await import('xlsx');
        
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = options.sheetName || workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet) {
          resolve({
            data: [],
            errors: [`Sheet "${sheetName}" not found in the file.`],
            warnings: [],
          });
          return;
        }

        // Convert to JSON with header row handling
        // First try with raw: true to get numeric types
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "", // XLSX seems to work better with empty string as default
          raw: true, // Use raw values to preserve numeric types
          range: options.headerRow || 0,
          blankrows: true, // Include blank rows
        }) as any[][];
        console.log("=== Excel Import Debug ===");
        console.log("Total rows:", jsonData.length);
        console.log("First 5 rows:", jsonData.slice(0, 5));
        console.log("Expected columns:", options.expectedColumns);

        // Try to detect red/colored cells by checking cell formatting
        const redRowIndices = new Set<number>();
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

        for (let R = range.s.r; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellAddress];

            if (cell && cell.s && cell.s.font && cell.s.font.color) {
              const color = cell.s.font.color;
              // Check if font color is red (RGB or theme)
              if ((color.rgb && (color.rgb.toLowerCase().includes('ff0000') || color.rgb.toLowerCase().includes('red'))) ||
                  (color.theme && color.theme === 2)) { // Theme 2 is often red
                redRowIndices.add(R);
                console.log(`Detected red text in row ${R}, cell ${cellAddress}`);
              }
            }
          }
        }

        console.log("Rows with red text:", Array.from(redRowIndices));

        if (jsonData.length === 0) {
          resolve({
            data: [],
            errors: ["The file appears to be empty."],
            warnings: [],
          });
          return;
        }

        // Auto-detect header row by finding row with most expected columns
        let headerRowIndex = 0;
        let maxMatches = 0;

        for (let i = 0; i < Math.min(10, jsonData.length); i++) {
          const row = jsonData[i] as string[];
          const matches = options.expectedColumns.filter(col =>
            row.some(cell => cell && cell.toString().trim() === col)
          ).length;

          console.log(`Row ${i} matches: ${matches}/${options.expectedColumns.length}`);
          console.log(`Row ${i} content:`, row);

          if (matches > maxMatches) {
            maxMatches = matches;
            headerRowIndex = i;
          }

          // If we found all expected columns, use this row
          if (matches === options.expectedColumns.length) {
            break;
          }
        }

        console.log(`Using row ${headerRowIndex} as header (${maxMatches} matches)`);

        const headers = jsonData[headerRowIndex] as string[];
        let allDataRows = jsonData.slice(headerRowIndex + 1);

        console.log("Headers:", headers);
        console.log("All data rows count:", allDataRows.length);
        console.log("First data row:", allDataRows[0]?.slice(0, 8));

        // Filter out rows based on:
        // 1. Skip 2nd row after headers (index 1) - contains totals/note
        // 2. Red text detection (if available)
        // 3. Rows with notes
        const dataRows = allDataRows.filter((row, idx) => {
          const actualRowIndex = headerRowIndex + 1 + idx;

          // ALWAYS skip the 2nd row after headers (index 1)
          if (idx === 1) {
            console.log(`Skipping 2nd row ${idx} (index ${actualRowIndex}):`, row.slice(0, 8));
            return false;
          }

          // Skip rows detected as having red text
          if (redRowIndices.has(actualRowIndex)) {
            console.log(`Skipping row ${idx} (index ${actualRowIndex}) - has red text:`, row.slice(0, 8));
            return false;
          }

          // Skip rows with "check these volumes" or similar text
          const rowText = row.slice(0, 5).map(cell => String(cell || '').toLowerCase()).join(' ');
          if (rowText.includes('check') || rowText.includes('volume') || rowText.includes('correct') || rowText.includes('plots')) {
            console.log(`Skipping note row ${idx}:`, row.slice(0, 5));
            return false;
          }

          return true;
        });

        console.log("Data rows count after all filtering:", dataRows.length);

        // Validate headers
        const errors: string[] = [];
        const warnings: string[] = [];
        const missingColumns = options.expectedColumns.filter(
          (col) => !headers.includes(col)
        );

        if (missingColumns.length > 0) {
          warnings.push(
            `Missing expected columns: ${missingColumns.join(", ")}`
          );
        }

        // Convert rows to objects
        const importedData: T[] = dataRows
          .filter((row) =>
            row.some((cell) => cell !== null && cell !== undefined && cell !== "")
          ) // Skip truly empty rows
          .map((row, index) => {
            const item: any = {};

            if (index < 3) {
              console.log(`\n=== Processing data row ${index} ===`);
              console.log(`Raw row data:`, row);
              console.log(`Headers:`, headers);
              console.log(`Column mapping:`, options.columnMapping);
            }

            headers.forEach((header, colIndex) => {
              if (header && header.trim()) {
                const value = row[colIndex];
                const headerKey = header.trim();

                // Use column mapping if provided, otherwise use header as key
                const dataKey = options.columnMapping?.[headerKey] || headerKey;

                if (index < 3) {
                  console.log(`  Col ${colIndex}: "${headerKey}" -> "${dataKey}" = ${value}`);
                }

                // Handle the case where the column doesn't exist in this row
                // XLSX might not include trailing empty columns in the row array
                if (colIndex >= row.length) {
                  item[dataKey] = null;
                } else {
                  const preserveEmpty = options.preserveEmptyValues !== false; // Default to true
                  item[dataKey] = convertValueType(value, preserveEmpty);
                }
              }
            });

            if (index < 3) {
              console.log(`Result object:`, item);
              console.log(`  - plotNo: "${item['plotNo']}" (type: ${typeof item['plotNo']})`);
              console.log(`  - housetype: "${item['housetype']}" (type: ${typeof item['housetype']})`);
              console.log(`  - foundationType: "${item['foundationType']}" (type: ${typeof item['foundationType']})`);
            }

            return item;
          });

        console.log(`=== Import Complete ===`);
        console.log(`Total imported rows: ${importedData.length}`);
        console.log(`Errors: ${errors.length}`, errors);
        console.log(`Warnings: ${warnings.length}`, warnings);

        resolve({
          data: importedData,
          errors,
          warnings,
        });
      } catch (error) {
        resolve({
          data: [],
          errors: [
            `Failed to parse Excel file: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          ],
          warnings: [],
        });
      }
    };

    reader.onerror = (error) => {
      resolve({
        data: [],
        errors: [`Failed to read the file: ${error}`],
        warnings: [],
      });
    };

    reader.onabort = () => {
      resolve({
        data: [],
        errors: ["File reading was aborted."],
        warnings: [],
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Generate template Excel file for import
 */
export const generateTemplate = async (
  filename: string,
  columns: ExcelColumn[],
  sampleData?: any[]
): Promise<void> => {
  // Load XLSX dynamically
  const XLSX = await import('xlsx');
  
  const headers = columns.map((col) => col.title);

  // Create sample data if not provided
  const templateData = sampleData || [
    columns.map(() => ""), // Empty row for template
  ];

  const worksheetData = [headers, ...templateData];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Auto-size columns
  const colWidths = columns.map((col) => ({
    wch: Math.max(col.title.length + 2, 15),
  }));
  worksheet["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

  const fileExtension = filename.toLowerCase().endsWith(".xlsx") ? "" : ".xlsx";
  XLSX.writeFile(workbook, `${filename}${fileExtension}`);
};

export interface MultiSheetExportOptions {
  filename: string;
  includeBranding?: boolean;
  projectName?: string;
  sheets: {
    name: string;
    columns: ExcelColumn[];
    data: any[];
  }[];
}

/**
 * Export data to Excel file with multiple worksheets
 */
export const exportToExcelMultiSheet = async (options: MultiSheetExportOptions): Promise<void> => {
  console.log("=== exportToExcelMultiSheet called ===");
  console.log("Options received:", options);
  
  const { filename, sheets, includeBranding = true, projectName } = options;

  if (!sheets || !Array.isArray(sheets)) {
    console.error("Invalid sheets:", sheets);
    throw new Error("Sheets must be an array");
  }

  if (sheets.length === 0) {
    console.error("Empty sheets array");
    throw new Error("No sheets to export");
  }

  try {
    console.log("Loading XLSX library...");
    const XLSX = await import('xlsx');
    console.log("XLSX library loaded successfully");

    console.log("Creating workbook...");
    // Create workbook
    const workbook = XLSX.utils.book_new();

  sheets.forEach((sheet, index) => {
    console.log(`Processing sheet ${index}:`, sheet.name);
    console.log(`Sheet columns count:`, sheet.columns?.length || 0);
    console.log(`Sheet data count:`, sheet.data?.length || 0);
    
    if (!sheet.columns || !Array.isArray(sheet.columns)) {
      console.error(`Invalid columns for sheet ${sheet.name}:`, sheet.columns);
      throw new Error(`Sheet ${sheet.name} has invalid columns`);
    }
    
    if (!sheet.data || !Array.isArray(sheet.data)) {
      console.error(`Invalid data for sheet ${sheet.name}:`, sheet.data);
      throw new Error(`Sheet ${sheet.name} has invalid data`);
    }
    
    // Prepare branding headers if enabled
    let brandingRows: string[][] = [];
    if (includeBranding) {
      brandingRows = [
        ["M & J EVANS CONSTRUCTION LIMITED", "", "", "LOGO: See M&J Company Logo"],
        ["The Groundworks Partner of Choice"],
        [sheet.name.toUpperCase()],
        projectName ? [`Project: ${projectName}`] : [],
        [`Generated: ${new Date().toLocaleDateString()}`],
        [`Report Type: Bill of Quantities Summary`],
        [] // Empty row
      ].filter(row => row.length > 0); // Remove empty arrays
    }

    // Prepare headers
    const headers = sheet.columns.map((col) => col.title);
    console.log(`Headers for ${sheet.name}:`, headers);

    const getNestedValue = (obj: any, path: string) => {
      console.log(`Getting nested value for path: "${path}" from object:`, obj);
      const keys = path.split(".");
      let result = obj;
      
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        console.log(`  Step ${i}: accessing key "${key}", current value:`, result);
        if (result === null || result === undefined) {
          console.log(`  Result is null/undefined at step ${i}`);
          return undefined;
        }
        result = result[key];
      }
      
      console.log(`  Final result for "${path}":`, result);
      return result;
    };

    // Prepare data rows
    console.log(`Processing ${sheet.data.length} data items for sheet ${sheet.name}`);
    const rows = sheet.data.map((item, itemIndex) => {
      console.log(`Processing data item ${itemIndex}:`, item);
      return sheet.columns.map((col, colIndex) => {
        // FIXED: The transformed data has flat keys, not nested objects
        // So we should access the value directly using the column key
        let value = item[col.key];
        
        // If direct access fails and it looks like a nested key, try the nested approach
        if (value === undefined && col.key.includes('.')) {
          value = getNestedValue(item, col.key);
        }
        
        const finalValue = col.render ? col.render(value) : value;
        
        if (itemIndex === 0 && colIndex < 10) {
          console.log(`Column ${colIndex} "${col.key}": raw=${value}, rendered=${finalValue}`);
        }
        
        return finalValue;
      });
    });
    
    console.log(`Generated ${rows.length} rows with ${rows[0]?.length || 0} columns each`);

    // Combine branding, headers and data
    const worksheetData = [...brandingRows, headers, ...rows];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns
    const colWidths = sheet.columns.map((col, index) => {
      const maxLength = Math.max(
        col.title.length,
        ...rows.map((row) => String(row[index] || "").length)
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    worksheet["!cols"] = colWidths;

    // Add worksheet to workbook (limit sheet name to 31 characters for Excel compatibility)
    const sheetName = sheet.name.substring(0, 31);
    console.log(`Adding sheet to workbook: ${sheetName}`);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  // Generate and download file
  const fileExtension = filename.toLowerCase().endsWith(".xlsx") ? "" : ".xlsx";
  const finalFilename = `${filename}${fileExtension}`;
  
  console.log("About to write file:", finalFilename);
  console.log("Workbook sheets:", workbook.SheetNames);
  
    try {
      XLSX.writeFile(workbook, finalFilename);
      console.log("File written successfully!");
    } catch (writeError) {
      console.error("Error writing file:", writeError);
      throw writeError;
    }
    
  } catch (error) {
    console.error("Error in exportToExcelMultiSheet:", error);
    alert(`Export failed: ${error.message}`);
    throw error;
  }
};

/**
 * Generate template Excel file with multiple worksheets
 */
export const generateMultiSheetTemplate = async (
  filename: string,
  sheets: { name: string; columns: ExcelColumn[] }[]
): Promise<void> => {
  // Load XLSX dynamically
  const XLSX = await import('xlsx');
  
  const workbook = XLSX.utils.book_new();

  sheets.forEach((sheet) => {
    const headers = sheet.columns.map((col) => col.title);
    
    // Create empty template row
    const templateData = [headers, sheet.columns.map(() => "")];
    
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);

    // Auto-size columns
    const colWidths = sheet.columns.map((col) => ({
      wch: Math.max(col.title.length + 2, 15),
    }));
    worksheet["!cols"] = colWidths;

    // Add worksheet to workbook (limit sheet name to 31 characters for Excel compatibility)
    const sheetName = sheet.name.substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const fileExtension = filename.toLowerCase().endsWith(".xlsx") ? "" : ".xlsx";
  XLSX.writeFile(workbook, `${filename}${fileExtension}`);
};

export interface MultiSheetImportOptions {
  expectedSheets: {
    name: string;
    expectedColumns: string[];
  }[];
  preserveEmptyValues?: boolean;
}

export interface MultiSheetImportResult<T = any> {
  sheets: {
    name: string;
    data: T[];
    errors: string[];
    warnings: string[];
  }[];
  globalErrors: string[];
}

/**
 * Import data from Excel file with multiple worksheets (NO VALIDATIONS)
 */
export const importFromExcelMultiSheet = async <T = any>(
  file: File,
  options: MultiSheetImportOptions
): Promise<MultiSheetImportResult<T>> => {
  return new Promise(async (resolve) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        console.log("=== Starting Excel Import (No Validations) ===");
        
        // Load XLSX dynamically
        const XLSX = await import('xlsx');
        
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        console.log("Available worksheets:", workbook.SheetNames);

        const result: MultiSheetImportResult<T> = {
          sheets: [],
          globalErrors: [], // No global errors for missing sheets
        };

        // Process ALL available sheets or expected sheets
        const sheetsToProcess = options.expectedSheets.length > 0 
          ? options.expectedSheets.map(s => s.name)
          : workbook.SheetNames;

        sheetsToProcess.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          
          if (!worksheet) {
            console.log(`Sheet "${sheetName}" not found - skipping`);
            return; // Skip missing sheets without error
          }

          console.log(`Processing sheet: ${sheetName}`);

          // Convert to JSON with header row handling
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false, // Convert numbers to strings to avoid type issues
            range: 0,
            blankrows: false, // Skip blank rows
          }) as any[][];

          if (jsonData.length === 0) {
            console.log(`Sheet "${sheetName}" is empty - skipping`);
            return; // Skip empty sheets without error
          }

          const headers = jsonData[0] as string[];
          const dataRows = jsonData.slice(1);

          console.log(`Sheet "${sheetName}" has ${headers.length} columns and ${dataRows.length} rows`);
          console.log("Headers:", headers);

          // NO VALIDATION - Convert all rows to objects directly
          const importedData: T[] = dataRows
            .filter((row) => 
              row.some((cell) => cell !== null && cell !== undefined && cell !== "")
            )
            .map((row, index) => {
              const item: any = {};
              headers.forEach((header, colIndex) => {
                if (header && header.trim()) {
                  const value = row[colIndex];
                  const headerKey = header.trim();
                  
                  // Simple conversion - no validation
                  if (value === null || value === undefined || value === "") {
                    item[headerKey] = null;
                  } else if (typeof value === "string" && !isNaN(Number(value)) && value.trim() !== "") {
                    // Convert numeric strings to numbers and round to 3 decimal places
                    item[headerKey] = Math.round(Number(value) * 1000) / 1000;
                  } else if (typeof value === "number") {
                    // Round numeric values to 3 decimal places
                    item[headerKey] = Math.round(value * 1000) / 1000;
                  } else {
                    item[headerKey] = value;
                  }
                }
              });
              return item;
            });

          console.log(`Imported ${importedData.length} rows from ${sheetName}`);
          if (importedData.length > 0) {
            console.log("First imported row:", importedData[0]);
          }

          result.sheets.push({
            name: sheetName,
            data: importedData,
            errors: [], // No errors
            warnings: [], // No warnings
          });
        });

        console.log("Import completed successfully");
        resolve(result);
        
      } catch (error) {
        console.error("Import error:", error);
        resolve({
          sheets: [],
          globalErrors: [
            `Failed to parse Excel file: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          ],
        });
      }
    };

    reader.onerror = (error) => {
      console.error("File reader error:", error);
      resolve({
        sheets: [],
        globalErrors: [`Failed to read the file: ${error}`],
      });
    };

    reader.onabort = () => {
      console.log("File reading aborted");
      resolve({
        sheets: [],
        globalErrors: ["File reading was aborted."],
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Validate imported data against expected structure
 */
export const validateImportedData = <T>(
  data: any[],
  validationRules: {
    [key: string]: {
      required?: boolean;
      type?: "string" | "number" | "boolean";
      validator?: (value: any) => boolean;
      errorMessage?: string;
    };
  }
): {
  validData: T[];
  errors: Array<{ row: number; field: string; message: string }>;
} => {
  const errors: Array<{ row: number; field: string; message: string }> = [];
  const validData: T[] = [];

  data.forEach((item, rowIndex) => {
    let isValid = true;
    const cleanedItem: any = {};

    Object.entries(validationRules).forEach(([field, rules]) => {
      const value = item[field];

      // Skip required field validation during import
      // Required validation should be handled at the application level, not during import

      // Handle all values that are not null/undefined
      // This includes 0, false, empty strings, and other "falsy" but valid values
      const isEmpty = value === undefined || value === null;

      if (!isEmpty) {
        let convertedValue = value;

        if (rules.type === "number") {
          // Handle different numeric formats and types
          if (typeof value === "number") {
            convertedValue = Math.round(value * 1000) / 1000; // Round to 3 decimal places
          } else if (typeof value === "string") {
            // Clean the string value (remove spaces, commas)
            const cleanedValue = value.toString().replace(/[,\s]/g, "");
            convertedValue = Number(cleanedValue);
            if (isNaN(convertedValue)) {
              errors.push({
                row: rowIndex + 1,
                field,
                message: `${field} must be a valid number`,
              });
              isValid = false;
              return;
            }
            convertedValue = Math.round(convertedValue * 1000) / 1000; // Round to 3 decimal places
          } else {
            convertedValue = Number(value);
            if (isNaN(convertedValue)) {
              errors.push({
                row: rowIndex + 1,
                field,
                message: `${field} must be a valid number`,
              });
              isValid = false;
              return;
            }
            convertedValue = Math.round(convertedValue * 1000) / 1000; // Round to 3 decimal places
          }
        } else if (rules.type === "boolean") {
          if (typeof value === "string") {
            const lowerValue = value.toLowerCase();
            convertedValue =
              lowerValue === "true" ||
              lowerValue === "yes" ||
              lowerValue === "1";
          } else {
            convertedValue = Boolean(value);
          }
        }

        // Custom validation
        if (rules.validator && !rules.validator(convertedValue)) {
          errors.push({
            row: rowIndex + 1,
            field,
            message: rules.errorMessage || `${field} has invalid value`,
          });
          isValid = false;
          return;
        }

        cleanedItem[field] = convertedValue;
      } else {
        // For empty values (null, undefined), always preserve them during import
        cleanedItem[field] = value; // Preserve the original empty value
      }
    });

    if (isValid) {
      validData.push(cleanedItem as T);
    }
  });

  return { validData, errors };
};

/**
 * Enhanced export function specifically for Bill of Quantities summaries with comprehensive branding
 */
export const exportBillSummaryToExcel = async (options: ExcelExportOptions): Promise<void> => {
  const enhancedOptions = {
    ...options,
    includeBranding: true,
    projectName: options.projectName || "Project Name Not Specified"
  };
  
  return exportToExcel(enhancedOptions);
};

/**
 * Enhanced multi-sheet export function specifically for Bill of Quantities summaries
 */
export const exportBillSummaryMultiSheet = async (options: MultiSheetExportOptions): Promise<void> => {
  const enhancedOptions = {
    ...options,
    includeBranding: true,
    projectName: options.projectName || "Project Name Not Specified"
  };
  
  return exportToExcelMultiSheet(enhancedOptions);
};
